import { useState, useEffect, useMemo } from 'react';
import { Edit2, Calendar, User, CheckCircle2, Clock, Archive, FileCheck, FileText, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { getAllBesoins, getBesoinHistorique, type BesoinData } from '../services/besoinsService';
import type { ClientItem } from '../services/clientService';
import BesoinRapportModal from './BesoinRapportModal';
import BesoinRapportCompletModal from './BesoinRapportCompletModal';
import type { PartiePrenante } from '../services/partiesPrenantesService';
import { getPartiePrenanteAvatarUrl } from '../services/partiesPrenantesService';
import { getIntakeTemplate } from '../domain/besoins/templates';
import { DOCUMENT_TYPES } from '../data/gestionDocumentaireTypes';

interface BesoinListSectionProps {
  search: string;
  clientFilter: string;
  visibilityFilter: string;
  templateFilter: string;
  clients: ClientItem[];
  onEdit: (besoinId: number) => void;
  partiesPrenantes: PartiePrenante[];
  allowedDomains?: string[];
}

const STATUT_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  brouillon: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: FileText },
  en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Clock },
  complet: { label: 'Complet', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  approuve_prisme: { label: 'Approuvé PRIsME', color: 'bg-purple-100 text-purple-700', icon: FileCheck },
  approuve_client: { label: 'Approuvé client', color: 'bg-indigo-100 text-indigo-700', icon: CheckCircle2 },
  archivé: { label: 'Archivé', color: 'bg-gray-200 text-gray-600', icon: Archive },
};

export default function BesoinListSection({
  search,
  clientFilter,
  visibilityFilter,
  templateFilter,
  clients,
  onEdit,
  partiesPrenantes,
  allowedDomains,
}: BesoinListSectionProps) {
  const [besoins, setBesoins] = useState<BesoinData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBesoinId, setSelectedBesoinId] = useState<number | null>(null);
  const [isRapportModalOpen, setIsRapportModalOpen] = useState(false);
  const [isRapportCompletOpen, setIsRapportCompletOpen] = useState(false);
  const [auditModalBesoinId, setAuditModalBesoinId] = useState<number | null>(null);
  const [auditHistory, setAuditHistory] = useState<any[] | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  const ppById = useMemo(() => {
    const map = new Map<number, PartiePrenante>();
    (partiesPrenantes || []).forEach((p) => {
      if (typeof p.id === 'number') map.set(p.id, p);
    });
    return map;
  }, [partiesPrenantes]);

  useEffect(() => {
    loadBesoins();
  }, []);

  // Recharger quand les filtres changent (pour rafraîchir si nécessaire)
  useEffect(() => {
    // Optionnel: recharger automatiquement toutes les 30 secondes
    const interval = setInterval(() => {
      loadBesoins();
    }, 30000); // 30 secondes

    return () => clearInterval(interval);
  }, []);

  const loadBesoins = async () => {
    setIsLoading(true);
    try {
      console.log('🔄 Chargement des besoins depuis l\'API...');
      const allBesoins = await getAllBesoins();
      console.log('📦 Réponse brute de getAllBesoins:', allBesoins);
      console.log('📦 Type de la réponse:', typeof allBesoins, Array.isArray(allBesoins));
      
      // S'assurer que c'est bien un tableau
      const besoinsArray = Array.isArray(allBesoins) ? allBesoins : [];
      console.log('✅ Besoins formatés (tableau):', besoinsArray.length, 'éléments');
      
      if (besoinsArray.length > 0) {
        console.log('📋 Premier besoin:', besoinsArray[0]);
        console.log('📋 Détails du premier besoin:', {
          id: besoinsArray[0].id,
          nom: besoinsArray[0].nom,
          client_id: besoinsArray[0].client_id,
          template_id: besoinsArray[0].template_id,
          statut: besoinsArray[0].statut,
        });
      }
      
      setBesoins(besoinsArray);
    } catch (error) {
      console.error('❌ Erreur chargement besoins:', error);
      setBesoins([]); // En cas d'erreur, initialiser à un tableau vide
    } finally {
      setIsLoading(false);
    }
  };

  const filteredBesoins = useMemo(() => {
    if (!Array.isArray(besoins)) {
      return [];
    }
    return besoins.filter((b) => {
      // Restriction par domaines (ex: Cybersécurité seulement)
      if (allowedDomains && allowedDomains.length > 0) {
        const template = getIntakeTemplate(b.template_id);
        const docTypeCode = template?.documentTypeCode;
        const docType = docTypeCode ? DOCUMENT_TYPES.find((t) => t.code === docTypeCode) : undefined;
        const domain = docType?.domain;
        if (!domain || !allowedDomains.includes(domain)) return false;
      }

      // Recherche
      const matchesSearch =
        !search ||
        b.nom?.toLowerCase().includes(search.toLowerCase()) ||
        b.description?.toLowerCase().includes(search.toLowerCase()) ||
        (b as any).client_nom?.toLowerCase().includes(search.toLowerCase());

      // Filtre client
      const matchesClient =
        clientFilter === 'tous' || b.client_id.toString() === clientFilter;

      // Filtre visibilité client
      const matchesVisibility =
        visibilityFilter === 'tous'
          ? true
          : visibilityFilter === 'visible'
          ? b.showpriseclient !== false
          : b.showpriseclient === false;

      // Filtre template (ou formulaires archivés)
      const matchesTemplate =
        templateFilter === 'archives'
          ? b.archived === true
          : (templateFilter === 'tous' || b.template_id === templateFilter) && b.archived !== true;

      return matchesSearch && matchesClient && matchesVisibility && matchesTemplate;
    });
  }, [besoins, search, clientFilter, visibilityFilter, templateFilter, clients, allowedDomains]);

  // Log pour déboguer
  useEffect(() => {
    console.log('🔍 BesoinListSection - État actuel:');
    console.log('  - besoins (state):', besoins.length, 'éléments');
    console.log('  - filteredBesoins:', filteredBesoins.length, 'éléments');
    console.log('  - Filtres:', { search, clientFilter, visibilityFilter, templateFilter });
    if (besoins.length > 0) {
      console.log('  - Premier besoin brut:', besoins[0]);
      console.log('  - Premier besoin filtré:', filteredBesoins[0] || 'Aucun (filtré)');
    }
  }, [besoins, filteredBesoins, search, clientFilter, visibilityFilter, templateFilter]);

  const getClientName = (clientId: number) => {
    if (!clients || !Array.isArray(clients)) {
      return `Client #${clientId}`;
    }
    const client = clients.find((c) => c.id === clientId);
    return client?.nom || `Client #${clientId}`;
  };

  const getStatutConfig = (statut?: string) => {
    if (!statut) return STATUT_CONFIG.brouillon;
    return STATUT_CONFIG[statut] || STATUT_CONFIG.brouillon;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getPPLabel = (ppId?: number | null) => {
    if (!ppId) return 'N/A';
    const p = ppById.get(ppId);
    if (!p) return `#${ppId}`;
    return `${p.prenom || ''} ${p.nom || ''}`.trim() || `#${ppId}`;
  };

  const UserAvatar = ({ ppId, title }: { ppId?: number | null; title: string }) => {
    if (!ppId) {
      return (
        <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-500" title={title}>
          ?
        </div>
      );
    }
    const p = ppById.get(ppId);
    const initials =
      p ? `${(p.prenom || '').slice(0, 1)}${(p.nom || '').slice(0, 1)}`.toUpperCase() : `${ppId}`.slice(0, 2);
    const canShowAvatar = !!p?.has_avatar;
    return (
      <div className="w-7 h-7 rounded-full overflow-hidden border border-gray-200 bg-gray-50" title={title}>
        {canShowAvatar ? (
          <img src={getPartiePrenanteAvatarUrl(ppId)} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-gray-600">
            {initials}
          </div>
        )}
      </div>
    );
  };

  const openAuditModal = async (besoinId: number) => {
    setAuditModalBesoinId(besoinId);
    setAuditLoading(true);
    setAuditError(null);
    setAuditHistory(null);
    try {
      const hist = await getBesoinHistorique(besoinId);
      setAuditHistory(hist || []);
    } catch (e: any) {
      setAuditError(e?.message || 'Erreur chargement historique');
    } finally {
      setAuditLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <p className="text-sm text-gray-600">Chargement des prises de besoin...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-100 bg-emerald-50">
        <div className="hidden md:grid [grid-template-columns:minmax(0,2.6fr)_minmax(0,1.8fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,1.6fr)_minmax(0,2fr)] gap-2 text-[11px] font-semibold text-gray-500">
          <div>Nom</div>
          <div>Client</div>
          <div>Template</div>
          <div>Statut</div>
          <div>Complétion</div>
          <div>Visibilité client</div>
          <div>Date création</div>
          <div className="flex items-center justify-end gap-2">
            <span>Actions</span>
            <button
              onClick={loadBesoins}
              disabled={isLoading}
              className="px-2 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        {/* Vue mobile simple */}
        <div className="md:hidden flex items-center justify-between text-[11px] text-gray-600">
          <span className="font-semibold">Prises de besoin</span>
          <button
            onClick={loadBesoins}
            disabled={isLoading}
            className="px-2 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {filteredBesoins.length === 0 ? (
          <div className="px-4 py-6 text-center text-[11px] text-gray-500 space-y-2">
            {besoins.length === 0 ? (
              <>
                <p>Aucune prise de besoin enregistrée</p>
                <p className="text-[10px] text-gray-400">Total dans la BD: {besoins.length}</p>
              </>
            ) : (
              <>
                <p>Aucune prise de besoin ne correspond aux filtres actuels</p>
                <p className="text-[10px] text-gray-400">
                  Total dans la BD: {besoins.length} • Filtrées: {filteredBesoins.length}
                </p>
                <button
                  onClick={() => {
                    console.log('🔍 Debug - Tous les besoins:', besoins);
                    console.log('🔍 Debug - Filtres:', { search, clientFilter, visibilityFilter, templateFilter });
                    besoins.forEach((b, i) => {
                      console.log(`Besoin ${i + 1}:`, {
                        id: b.id,
                        nom: b.nom,
                        client_id: b.client_id,
                        statut: b.statut,
                        template_id: b.template_id,
                        showpriseclient: (b as any).showpriseclient,
                      });
                    });
                  }}
                  className="mt-2 px-2 py-1 text-[10px] bg-gray-100 rounded text-gray-600 hover:bg-gray-200"
                >
                  Voir les détails dans la console
                </button>
              </>
            )}
          </div>
        ) : (
          filteredBesoins.map((besoin) => {
            const statutConfig = getStatutConfig(besoin.statut);
            const StatutIcon = statutConfig.icon;
            const completion = besoin.metadata?.completion_percentage || 0;

            return (
              <div
                key={besoin.id}
                className="px-4 py-3 text-xs hover:bg-emerald-50/30 transition-colors grid grid-cols-1 md:[grid-template-columns:minmax(0,2.6fr)_minmax(0,1.8fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,1.6fr)_minmax(0,2fr)] gap-2 items-center"
              >
                <div>
                  <p className="font-semibold text-gray-900">{besoin.nom || 'Sans nom'}</p>
                  <p className="md:hidden text-[11px] text-gray-500 mt-0.5">
                    {getClientName(besoin.client_id)} • {besoin.template_id}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <UserAvatar ppId={besoin.created_by} title={`Créé par: ${getPPLabel(besoin.created_by)}`} />
                      <span>Créé</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1.5">
                      <UserAvatar ppId={besoin.last_modified_by} title={`Modifié par: ${getPPLabel(besoin.last_modified_by)}`} />
                      <span>Modifié</span>
                    </div>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2">
                  <User className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-700">{getClientName(besoin.client_id)}</span>
                </div>
                <div className="hidden md:block">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-[10px] font-medium text-gray-700">
                    {besoin.template_id}
                  </span>
                </div>
                <div className="hidden md:flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statutConfig.color}`}
                  >
                    <StatutIcon className="w-3 h-3" />
                    {statutConfig.label}
                  </span>
                </div>
                <div className="hidden md:block">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          completion === 100
                            ? 'bg-emerald-500'
                            : completion >= 50
                            ? 'bg-blue-500'
                            : 'bg-gray-400'
                        }`}
                        style={{ width: `${completion}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-600 font-medium">{completion}%</span>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-1">
                  {besoin.showpriseclient !== false ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-[10px] font-medium text-emerald-700 border border-emerald-200">
                      <Eye className="w-3 h-3" />
                      Visible client
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[10px] font-medium text-gray-600 border border-gray-200">
                      <EyeOff className="w-3 h-3" />
                      Caché client
                    </span>
                  )}
                </div>
                <div className="hidden md:flex items-center gap-2 text-[11px] text-gray-600">
                  <Calendar className="w-3 h-3 text-gray-400" />
                  {formatDate(besoin.created_at)}
                </div>
                <div className="flex flex-wrap gap-1 justify-start md:justify-end">
                  <button
                    onClick={() => besoin.id && openAuditModal(besoin.id)}
                    className="px-3 py-1 rounded-full border border-gray-300 text-gray-700 bg-white text-[11px] font-semibold hover:bg-gray-100 transition-colors flex items-center gap-1"
                    type="button"
                  >
                    Historique
                  </button>
                  <button
                    onClick={() => {
                      if (besoin.id) {
                        setSelectedBesoinId(besoin.id);
                        setIsRapportModalOpen(true);
                      }
                    }}
                    className="px-3 py-1 rounded-full border border-blue-500/60 text-blue-700 bg-white text-[11px] font-semibold hover:bg-blue-500 hover:text-white transition-colors flex items-center gap-1"
                  >
                    <Eye className="w-3 h-3" />
                    Voir le rapport
                  </button>
                  <button
                    onClick={() => {
                      if (besoin.id) {
                        setSelectedBesoinId(besoin.id);
                        setIsRapportCompletOpen(true);
                      }
                    }}
                    className="px-3 py-1 rounded-full border border-bz-blue/40 text-bz-blue bg-white text-[11px] font-semibold hover:bg-bz-blue/5 transition-colors flex items-center gap-1"
                  >
                    <Eye className="w-3 h-3" />
                    Rapport complet
                  </button>
                  <button
                    onClick={() => besoin.id && onEdit(besoin.id)}
                    className="px-3 py-1 rounded-full border border-emerald-500/60 text-emerald-700 bg-white text-[11px] font-semibold hover:bg-emerald-500 hover:text-white transition-colors flex items-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" />
                    Éditer
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de rapport synthèse */}
      {selectedBesoinId && (
        <>
          <BesoinRapportModal
            isOpen={isRapportModalOpen}
            onClose={() => {
              setIsRapportModalOpen(false);
              setSelectedBesoinId(null);
            }}
            besoinId={selectedBesoinId}
          />
          <BesoinRapportCompletModal
            isOpen={isRapportCompletOpen}
            onClose={() => {
              setIsRapportCompletOpen(false);
              setSelectedBesoinId(null);
            }}
            besoinId={selectedBesoinId}
          />
        </>
      )}

      {/* Modal historique */}
      {auditModalBesoinId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Historique — prise de besoin #{auditModalBesoinId}</h2>
                <p className="text-xs text-gray-600">Créations/modifications (versions) avec auteur et date.</p>
              </div>
              <button
                onClick={() => {
                  setAuditModalBesoinId(null);
                  setAuditHistory(null);
                  setAuditError(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="text-2xl text-gray-500">&times;</span>
              </button>
            </div>
            <div className="p-6 space-y-3">
              {auditLoading && <div className="text-sm text-gray-600">Chargement…</div>}
              {auditError && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{auditError}</div>}
              {!auditLoading && !auditError && (
                <div className="space-y-2">
                  {(auditHistory || []).length === 0 ? (
                    <div className="text-sm text-gray-600">Aucune entrée d’historique.</div>
                  ) : (
                    (auditHistory || []).map((h, idx) => (
                      <div key={idx} className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <UserAvatar ppId={h.modified_by} title={`Modifié par: ${getPPLabel(h.modified_by)}`} />
                            <div>
                              <div className="text-sm font-semibold text-gray-900">{getPPLabel(h.modified_by)}</div>
                              <div className="text-xs text-gray-600">
                                Version {h.version} • {formatDateTime(h.modified_at)}
                              </div>
                            </div>
                          </div>
                          {h.commentaire && (
                            <div className="text-xs text-gray-700 max-w-[55%]">
                              <span className="font-semibold">Note:</span> {h.commentaire}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

